import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Bell, Users, UserCheck, Send } from 'lucide-react';
import UserSelect from '../components/UserSelect';

export default function AdminNotifications() {
  interface User {
    id: string;
    email: string;
    display_name: string | null;
    avatar_url: string | null;
    created_at: string;
    niche: string | null;
  }

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [notificationType, setNotificationType] = useState<'specific' | 'selected' | 'all'>('specific');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users');
        return;
      }

      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setFeedback({ type: '', message: '' });

    try {
      let emailsToSend: string[] = [];

      if (notificationType === 'specific' || notificationType === 'selected') {
        // For specific or selected users, use the selected user emails
        emailsToSend = selectedUsers;
      } else {
        // For all users, get all user emails
        emailsToSend = users.map(user => user.email);
      }

      // Prepare webhook payload
      const webhookPayload = {
        emails: emailsToSend,
        subject: subject,
        content: message
      };

      // Send to webhook
      const response = await fetch('https://workflows.thementorprogram.xyz/webhook-test/45180ff4-7a99-4e89-ac90-03f2fad21b3e', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      if (!response.ok) {
        throw new Error(`Webhook request failed with status: ${response.status}`);
      }

      // Also save notifications to database
      if (notificationType === 'specific' || notificationType === 'selected') {
        // For specific users, create individual notifications
        const notifications = selectedUsers.map(email => {
          const user = users.find(u => u.email === email);
          return {
            user_id: user?.id,
            title: subject,
            body: message,
            type: 'admin',
            is_read: false
          };
        });

        const { error: dbError } = await supabase
          .from('notifications')
          .insert(notifications);

        if (dbError) {
          console.error('Error saving notifications to database:', dbError);
          // Don't throw here - webhook succeeded, just log the DB error
        }
      } else {
        // For all users, create one notification without user_id
        const { error: dbError } = await supabase
          .from('notifications')
          .insert({
            user_id: null, // This will make it visible to all users
            title: subject,
            body: message,
            type: 'admin',
            is_read: false
          });

        if (dbError) {
          console.error('Error saving notification to database:', dbError);
          // Don't throw here - webhook succeeded, just log the DB error
        }
      }

      setFeedback({
        type: 'success',
        message: `Notification sent successfully to ${emailsToSend.length} recipient${emailsToSend.length > 1 ? 's' : ''}!`
      });
      
      // Reset form
      setSubject('');
      setMessage('');
      setSelectedUsers([]);
      
    } catch (error: any) {
      console.error('Error sending notification:', error);
      setFeedback({
        type: 'error',
        message: error.message || 'Failed to send notification'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}
      <div className="flex items-center mb-8">
        <Bell className="w-8 h-8 text-[#c9fffc] mr-3" />
        <h1 className="text-2xl font-bold text-white">Admin Notifications</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Notification Type Selection */}
        <div className="grid grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => setNotificationType('specific')}
            className={`p-4 rounded-xl border transition-all ${
              notificationType === 'specific'
                ? 'border-[#c9fffc] bg-[#c9fffc]/10 text-[#c9fffc]'
                : 'border-gray-700 hover:border-[#c9fffc]/50'
            }`}
          >
            <UserCheck className="w-6 h-6 mx-auto mb-2" />
            <span className="block text-sm">Specific User</span>
          </button>
          
          <button
            type="button"
            onClick={() => setNotificationType('selected')}
            className={`p-4 rounded-xl border transition-all ${
              notificationType === 'selected'
                ? 'border-[#c9fffc] bg-[#c9fffc]/10 text-[#c9fffc]'
                : 'border-gray-700 hover:border-[#c9fffc]/50'
            }`}
          >
            <Users className="w-6 h-6 mx-auto mb-2" />
            <span className="block text-sm">Selected Users</span>
          </button>
          
          <button
            type="button"
            onClick={() => setNotificationType('all')}
            className={`p-4 rounded-xl border transition-all ${
              notificationType === 'all'
                ? 'border-[#c9fffc] bg-[#c9fffc]/10 text-[#c9fffc]'
                : 'border-gray-700 hover:border-[#c9fffc]/50'
            }`}
          >
            <Send className="w-6 h-6 mx-auto mb-2" />
            <span className="block text-sm">All Users</span>
          </button>
        </div>

        {/* User Selection */}
        {(notificationType === 'specific' || notificationType === 'selected') && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              Select Recipients
            </label>
            <UserSelect
              users={users}
              selectedUsers={selectedUsers}
              onSelect={setSelectedUsers}
              multiple={notificationType === 'selected'}
            />
          </div>
        )}

        {/* Subject and Message */}
        <div className="space-y-4">
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-1">
              Subject
            </label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-xl focus:border-[#c9fffc] focus:ring-1 focus:ring-[#c9fffc] text-white"
              required
            />
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
              Message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-xl focus:border-[#c9fffc] focus:ring-1 focus:ring-[#c9fffc] text-white"
              required
            />
          </div>
        </div>

        {/* Feedback Message */}
        {feedback.message && (
          <div className={`p-4 rounded-xl ${
            feedback.type === 'success' ? 'bg-green-500/10 text-green-300' : 'bg-red-500/10 text-red-300'
          }`}>
            {feedback.message}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || (
            (notificationType !== 'all' && selectedUsers.length === 0) ||
            !subject ||
            !message
          )}
          className="w-full py-3 px-4 bg-[#c9fffc]/10 hover:bg-[#c9fffc]/20 text-[#c9fffc] rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Sending...' : 'Send Notification'}
        </button>
      </form>
    </div>
  );
}
